/**
 * Triveny.ai — full-bleed home / launcher.
 *
 * Replaces the WeaveIn marketing Homepage for authenticated Triveny users at
 * app.triveny.ai/. Three pillars (Design · Market · Engage) each surface a
 * primary launcher + two secondary actions; pressing the primary launcher
 * (or Enter) opens a focused panel with a cosmetic preview of the use case.
 *
 * "My Day" lives in the top nav as the destination to the existing
 * TrivenyMyDay surface (activeTab="platform-dashboard").
 *
 * Source design: design_handoff_platform_redesign/home/index.html — ported
 * with structural styles in src/styles/triveny-home.css.
 */

import React, { useCallback, useEffect, useState } from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  LayoutTemplate,
  Mail,
  ClipboardList,
  Send,
  Users,
  BarChart3,
  PhoneCall,
  Inbox,
  CalendarClock,
  ArrowRight,
  Sun,
  LogOut,
  SlidersHorizontal,
  User,
  Target,
  Eye,
  KanbanSquare,
  Brain,
  Headphones,
  PlusCircle,
} from 'lucide-react';
import ConsentDocViewer from './ConsentDocViewer';
import { setPendingPrompt, PROMPT_SURFACES } from '../../shared/stores/promptHandoff';

// ----------------------------------------------------------------------------
// Pillar config — keeps content driven from data, not duplicated in JSX
// ----------------------------------------------------------------------------

type Pillar = 'Design' | 'Market' | 'Engage';

interface PillarSecondary {
  icon: LucideIcon;
  title: string;
  /** activeTab to navigate to when this secondary chip is clicked */
  tab: string;
  /** Optional sub-tab hint forwarded via pending-prompt store */
  intent?: string;
}

interface PillarSpec {
  eyebrow: string;
  title: string;
  sub: string;
  primary: { icon: LucideIcon; title: string; sub: string };
  /** activeTab the CTA navigates to; the modal closes first */
  ctaTab: string;
  /** Optional intent passed to the destination page (e.g. 'landing', 'email') */
  ctaIntent?: string;
  secondary: PillarSecondary[];
  panelIcon: LucideIcon;
  cta: { label: string; spark: boolean };
}

const PILLARS: Record<Pillar, PillarSpec> = {
  // Design = Studio. Modal hosts the prompt+preview composer; CTA hands the
  // prompt off to WebPresencePage (AI Studio tab) via the prompt-handoff
  // sessionStorage shim so the user lands in Studio with their text intact.
  Design: {
    eyebrow: 'Design',
    title: 'Bring it to life.',
    sub: 'Pages, emails, and forms that look like you made them on purpose — generated from a prompt, on your brand.',
    primary: { icon: LayoutTemplate, title: 'Create a landing page', sub: 'Prompt → published page in minutes' },
    ctaTab: 'platform-web-presence',
    ctaIntent: 'landing',
    secondary: [
      { icon: Mail, title: 'Design an email', tab: 'platform-web-presence', intent: 'email' },
      // Forms live in Studio's Manage › Forms tab — open Studio there rather
      // than the standalone (WeaveIn) form-builder route.
      { icon: ClipboardList, title: 'Build a form', tab: 'platform-web-presence', intent: 'forms' },
    ],
    panelIcon: LayoutTemplate,
    cta: { label: 'Generate page', spark: true },
  },
  // Market = acquisition. Primary launches a multichannel campaign; the
  // five secondaries map onto the AI-native funnel:
  //   ICP profile (Lead Builder) → audience build (Cold Outreach + Visitor ID)
  //   → campaign (Marketing) → analytics (Marketing/analytics tab).
  Market: {
    eyebrow: 'Market',
    title: 'Reach the right people.',
    sub: 'Define your ICP, build the right audience, write once, and send across email, SMS, and voice — then watch what works.',
    primary: { icon: Send, title: 'Launch a campaign', sub: 'Email · SMS · voice, one flow' },
    ctaTab: 'platform-marketing',
    ctaIntent: 'campaigns',
    secondary: [
      { icon: Target, title: 'Define an ICP', tab: 'platform-lead-builder', intent: 'icp-profiles' },
      { icon: Users, title: 'Build a lead list', tab: 'platform-lead-builder', intent: 'companies' },
      { icon: Eye, title: 'Identify visitors', tab: 'platform-visitor-id' },
      { icon: PhoneCall, title: 'Cold outreach', tab: 'platform-cold-outreach' },
      { icon: BarChart3, title: 'View analytics', tab: 'platform-marketing-analytics' },
    ],
    panelIcon: Send,
    cta: { label: 'Launch campaign', spark: false },
  },
  // Engage = conversion. Pipeline-first; surfaces AI sales intel
  // (Intelligence single-vendor matches) and customer support inline so the
  // deal-cycle and the post-sale cycle live together. "Inbox" stays for
  // unified messaging, "Schedule activity" hands off to the Pipeline page's
  // activities tab.
  Engage: {
    eyebrow: 'Engage',
    title: 'Move deals forward.',
    sub: 'Pipeline, opportunities, and AI-coached activities — with customer support inline to close every loop.',
    primary: { icon: KanbanSquare, title: 'Manage pipeline', sub: 'Kanban · activities · AI next-best-action' },
    ctaTab: 'platform-sales',
    secondary: [
      { icon: Brain, title: 'Coach a deal (AI)', tab: 'platform-intelligence', intent: 'matches' },
      { icon: PlusCircle, title: 'New opportunity', tab: 'platform-sales', intent: 'new' },
      { icon: CalendarClock, title: 'Schedule activity', tab: 'platform-sales', intent: 'activities' },
      { icon: Inbox, title: 'Open the inbox', tab: 'workspace-messaging' },
      { icon: Headphones, title: 'Customer support', tab: 'platform-support' },
    ],
    panelIcon: KanbanSquare,
    cta: { label: 'Open pipeline', spark: false },
  },
};

const ORDER: Pillar[] = ['Design', 'Market', 'Engage'];

// ----------------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------------

function initialsFromName(name?: string | null): string {
  if (!name) return 'TV';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'TV';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// ----------------------------------------------------------------------------
// Top-level component
// ----------------------------------------------------------------------------

export interface TrivenyHomeProps {
  /** Tab id to route to when the user clicks "My Day" in the nav. */
  onNavigate: (tab: string) => void;
  /** Avatar/profile click handler — opens the profile drawer. */
  onShowProfile?: () => void;
  /** Sign out — wired to AuthContext.logout in App.tsx. */
  onLogout?: () => void;
  /** Display name for deriving avatar initials. */
  userName?: string | null;
  /** Tenant / org name displayed in the top nav so the user knows which workspace they're in. */
  tenantName?: string | null;
  /** Pillar to open on — derived from where the user came from so returning
      home lands on the matching tab instead of always defaulting to Design. */
  initialPillar?: Pillar;
}

export default function TrivenyHome({ onNavigate, onShowProfile, onLogout, userName, tenantName, initialPillar }: TrivenyHomeProps) {
  const [active, setActive] = useState<Pillar>(initialPillar ?? 'Design');
  // Avatar dropdown menu — collapses My Day / Inbox / Preferences /
  // View profile / Sign out under the user initials chip so the top
  // nav stays focused on logo + Design / Market / Engage.
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  // Footer Privacy + AI Disclaimers modal — fetches active doc from
  // /api/consent/documents/{type} per migration 389.
  const [docType, setDocType] = useState<'privacy_policy' | 'ai_disclaimers' | null>(null);

  // Primary CTA + Enter both navigate straight to the active pillar's
  // destination app page (Studio / Marketing / Pipeline) — no interim
  // preview modal. platform-web-presence opens on the AI Studio tab by
  // default, so a direct nav lands the user where the old modal's CTA did.
  const goToActive = useCallback(() => {
    onNavigate(PILLARS[active].ctaTab);
  }, [active, onNavigate]);

  // Secondary chips: Studio sub-surfaces (forms / email) are selected via the
  // prompt-handoff store that WebPresencePage consumes on mount. Stash it
  // synchronously before navigating so the destination reads it on first render.
  const goToSecondary = useCallback(
    (tab: string, intent?: string) => {
      if (tab === 'platform-web-presence' && intent) {
        setPendingPrompt(PROMPT_SURFACES.studio, { prompt: '', intent });
      }
      onNavigate(tab);
    },
    [onNavigate],
  );

  // Enter activates the active pillar's primary CTA (matches the ↵ hint on
  // the launcher).
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Enter') return;
      // Don't hijack Enter inside text inputs or buttons that the user clicked.
      const t = e.target as HTMLElement | null;
      const tag = t?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || t?.isContentEditable) return;
      goToActive();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [goToActive]);

  const p = PILLARS[active];
  const PrimaryIcon = p.primary.icon;
  const initials = initialsFromName(userName);

  return (
    // data-pillar drives the boat-drift rest position in
    // src/styles/triveny-home.css. Selecting a pillar above nudges the
    // boat in the direction the user is working: Design left, Market
    // center, Engage right. Safe to remove the attribute; the stage
    // animation falls back to its default 50% rest position.
    <div className="triveny-home" data-pillar={active}>
      <div className="stage">
        {/* Water ripple overlay — two repeating wave bands drifting
            leftward at different speeds, sitting above the boats in
            home-bg.jpg. Reinforces the sense of current under the
            fleet without needing a transparent-cutout asset.
            Removing this <svg> safely reverts to the static bg. */}
        <svg
          className="water-ripple"
          aria-hidden="true"
          viewBox="0 0 1620 540"
          preserveAspectRatio="none"
        >
          <defs>
            {/* Two horizontally-tiling wave patterns. tileWidth (60)
                MUST match the translateX in @keyframes triveny-water-ripple
                so the loop is seamless. */}
            <pattern id="tv-ripple-near" x="0" y="0" width="60" height="6" patternUnits="userSpaceOnUse">
              <path d="M0 3 Q15 0 30 3 T60 3" fill="none" stroke="#fff" strokeWidth="0.8" strokeLinecap="round" />
            </pattern>
            <pattern id="tv-ripple-far" x="0" y="0" width="60" height="6" patternUnits="userSpaceOnUse">
              <path d="M0 3 Q15 1.5 30 3 T60 3" fill="none" stroke="#cbd5e1" strokeWidth="0.6" strokeLinecap="round" />
            </pattern>
          </defs>
          {/* Far ripples (smaller, lighter, slower) sit higher on the
              water plane — perspective. Near ripples (bigger, whiter,
              faster) sit at the bottom. */}
          <g className="ripple-far">
            <rect x="-60" y="120" width="1740" height="6" fill="url(#tv-ripple-far)" />
            <rect x="-60" y="180" width="1740" height="6" fill="url(#tv-ripple-far)" />
            <rect x="-60" y="260" width="1740" height="6" fill="url(#tv-ripple-far)" />
          </g>
          <g className="ripple-near">
            <rect x="-60" y="360" width="1740" height="6" fill="url(#tv-ripple-near)" />
            <rect x="-60" y="430" width="1740" height="6" fill="url(#tv-ripple-near)" />
            <rect x="-60" y="500" width="1740" height="6" fill="url(#tv-ripple-near)" />
          </g>
        </svg>
        <nav className="topnav">
          {/* Logo: PNG wordmark + spark mark. The dark "triveny" text reads
              cleanly on the home page's paper-white background. For dark
              mode, src/styles/triveny-home.css applies a filter inversion
              that flips the black text to white while keeping the colorful
              spark approximately on-palette. Fallback: SVG text wordmark
              (kept for one render in case the PNG fails to load). */}
          {/* Triveny logo: brand-supplied SVG. Dark variant
              (logo.svg) on light bg; white variant (logo-white.svg) when
              the page is in dark mode. Source-swap via a small effect
              rather than a CSS filter so the spark colors render natively
              (no inversion artifacts). */}
          <img
            className="wm"
            src="/triveny/logo.svg"
            alt="triveny"
            ref={(el) => {
              if (!el) return;
              const isDark = document.documentElement.classList.contains('dark');
              el.src = isDark ? '/triveny/logo-white.svg' : '/triveny/logo.svg';
            }}
            onError={(e) => {
              (e.target as HTMLImageElement).src = '/triveny/wordmark.svg';
            }}
          />
          <div className="nav-center">
            {ORDER.map((pillar) => (
              <button
                key={pillar}
                data-pillar={pillar}
                className={'nav-link' + (active === pillar ? ' active' : '')}
                onClick={() => setActive(pillar)}
              >
                {pillar}
              </button>
            ))}
          </div>
          {/* Avatar-only top nav. The previous My Day / Inbox / Preferences
              buttons cluttered the bar; they now live in the dropdown that
              opens on avatar click. Tenant name moved to the footer to keep
              the header focused on logo + pillar tabs + avatar. */}
          <div className="nav-right" style={{ position: 'relative' }}>
            <button
              className="avatar"
              onClick={() => setUserMenuOpen((o) => !o)}
              aria-haspopup="menu"
              aria-expanded={userMenuOpen}
              aria-label={userName ? `${userName} menu` : 'Account menu'}
              title={userName ? `${userName} — open menu` : 'Account menu'}
            >
              {initials}
            </button>
            {userMenuOpen && (
              <div
                className="user-menu"
                role="menu"
                onMouseLeave={() => setUserMenuOpen(false)}
              >
                {userName && <div className="head">{userName}</div>}
                <button
                  className="item"
                  role="menuitem"
                  onClick={() => { setUserMenuOpen(false); onNavigate('platform-dashboard'); }}
                >
                  <Sun size={14} /> My Day
                </button>
                <button
                  className="item"
                  role="menuitem"
                  onClick={() => { setUserMenuOpen(false); onNavigate('workspace-messaging'); }}
                >
                  <Inbox size={14} /> Inbox
                </button>
                <button
                  className="item"
                  role="menuitem"
                  onClick={() => { setUserMenuOpen(false); onNavigate('user-preferences'); }}
                >
                  <SlidersHorizontal size={14} /> Preferences
                </button>
                <div className="sep" />
                <button
                  className="item"
                  role="menuitem"
                  onClick={() => { setUserMenuOpen(false); onShowProfile?.(); }}
                >
                  <User size={14} /> View profile
                </button>
                {onLogout && (
                  <button
                    className="item"
                    role="menuitem"
                    onClick={() => { setUserMenuOpen(false); onLogout(); }}
                  >
                    <LogOut size={14} /> Sign out
                  </button>
                )}
              </div>
            )}
          </div>
        </nav>

        <div className="hero-copy">
          {/* Eyebrow (spark logo + pillar word) intentionally removed so each
              pillar leads directly with its statement headline — e.g. Design
              opens on "Bring it to life." The pillar is already indicated by
              the active tab in the top nav. */}
          <h1 className="hero-title">{p.title}</h1>
          <p className="hero-sub">{p.sub}</p>
          <div className="launchers">
            <button className="launcher primary" onClick={goToActive}>
              <span className="ico">
                <PrimaryIcon size={16} />
              </span>
              <span className="txt">
                <span className="lt">{p.primary.title}</span>
                <span className="ls">{p.primary.sub}</span>
              </span>
              <span className="kbd">↵</span>
              <span className="arrow">
                <ArrowRight size={16} />
              </span>
            </button>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center' }}>
              {p.secondary.map((s) => {
                const SIcon = s.icon;
                return (
                  <button
                    key={s.title}
                    className="launcher"
                    style={{ minWidth: 0, padding: '8px 14px' }}
                    onClick={() => goToSecondary(s.tab, s.intent)}
                    title={`Go to ${s.title}`}
                  >
                    <span
                      className="ico"
                      style={{ width: 24, height: 24, background: 'var(--paper-2)', color: 'var(--ink-700)' }}
                    >
                      <SIcon size={13} />
                    </span>
                    <span className="lt" style={{ fontSize: 13 }}>
                      {s.title}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <footer className="footer">
          <span>© {new Date().getFullYear()} Triveny</span>
          <span className="sep">·</span>
          <span>All rights reserved</span>
          {tenantName && (
            <span className="tenant" title={tenantName}>{tenantName}</span>
          )}
          <div className="right">
            <a
              onClick={() => setDocType('privacy_policy')}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') setDocType('privacy_policy');
              }}
              style={{ cursor: 'pointer' }}
            >
              Privacy
            </a>
            <a
              onClick={() => setDocType('ai_disclaimers')}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') setDocType('ai_disclaimers');
              }}
              style={{ cursor: 'pointer' }}
            >
              AI disclaimers
            </a>
          </div>
        </footer>
      </div>

      <ConsentDocViewer
        open={docType !== null}
        documentType={docType ?? 'privacy_policy'}
        onClose={() => setDocType(null)}
      />
    </div>
  );
}
